import { SiteLayout } from "@/components/SiteLayout";
import { ArrowRight } from "lucide-react";
import { type Locale } from "@/i18n/config";
import { pageHead } from "@/i18n/head";
import { karriereStrings, EMPLY_JOBS_URL } from "@/i18n/content/karriere";
import { Img, SIZES } from "@/components/Img";

/** <head> for the careers page. */
export function karriereHead(locale: Locale) {
  const s = karriereStrings[locale];
  return pageHead("karriere", locale, s.meta, { breadcrumbName: s.hero.title });
}

export function KarrierePage({ locale }: { locale: Locale }) {
  const t = karriereStrings[locale];

  return (
    <SiteLayout>
      {/* Hero */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight">{t.hero.title}</h1>
          <p className="mt-6 text-lg text-foreground/85 leading-relaxed">{t.hero.p1}</p>
          <p className="mt-4 text-lg text-foreground/85">{t.hero.p2}</p>
        </div>
      </section>

      {/* Medarbejderne bag */}
      <section className="bg-section-light text-section-light-foreground py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-12 lg:grid-cols-2 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.team.heading}</h2>
            <p className="mt-6 leading-relaxed">{t.team.p1}</p>
            <p className="mt-4 leading-relaxed">{t.team.p2}</p>
            <p className="mt-4 leading-relaxed">{t.team.p3}</p>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {t.stats.map((s) => (
              <div key={s.label} className="rounded-2xl bg-background text-white p-6 text-center">
                <div className="text-3xl md:text-4xl font-bold text-white">{s.value}</div>
                <p className="mt-3 text-sm leading-snug text-white/90">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="py-20">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <p className="text-2xl md:text-3xl font-medium leading-snug text-foreground/95">
            “{t.quote1.text}”
          </p>
          <p className="mt-8 font-semibold">{t.quote1.name}</p>
          <p className="text-foreground/70 text-sm">{t.quote1.role}</p>
        </div>
      </section>

      {/* Lokal tilstedeværelse */}
      <section className="pb-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-12 lg:grid-cols-2 items-center">
          <div className="grid grid-cols-2 gap-4 order-2 lg:order-1">
            <Img
              src="/wp-content/uploads/MISupportIT_NTK4135-1-scaled.jpg"
              alt={t.local.imgAlt1}
              className="rounded-2xl w-full aspect-[3/4] object-cover"
              sizes={SIZES.portrait}
            />
            <Img
              src="/wp-content/uploads/Kontor-gruppe-10-scaled.jpg"
              alt={t.local.imgAlt2}
              className="rounded-2xl w-full aspect-[3/4] object-cover"
              sizes={SIZES.portrait}
            />
          </div>
          <div className="order-1 lg:order-2">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.local.heading}</h2>
            <p className="mt-6 text-foreground/90 leading-relaxed">{t.local.p1}</p>
            <p className="mt-4 text-foreground/90 leading-relaxed">{t.local.p2}</p>
          </div>
        </div>
      </section>

      {/* Projektindsigt */}
      <section className="bg-secondary py-20">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.insight.heading}</h2>
          <p className="mt-6 text-lg text-foreground/95 leading-relaxed">{t.insight.p1}</p>
          <p className="mt-4 text-lg text-foreground/95 leading-relaxed">{t.insight.p2}</p>
          <div className="mt-10 max-w-2xl mx-auto">
            <p className="text-xl italic text-foreground/95">“{t.quote2.text}”</p>
            <p className="mt-4 font-semibold">{t.quote2.name}</p>
            <p className="text-foreground/70 text-sm">{t.quote2.role}</p>
          </div>
        </div>
      </section>

      {/* Trivsel og sammenhold */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-12 lg:grid-cols-2 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.wellbeing.heading}</h2>
            <p className="mt-6 text-foreground/90 leading-relaxed">{t.wellbeing.p1}</p>
            <p className="mt-4 text-foreground/90 leading-relaxed">{t.wellbeing.p2}</p>
            <ul className="mt-6 space-y-3">
              {t.wellbeing.initiatives.map((i) => (
                <li key={i} className="flex gap-3 items-start">
                  <span className="mt-2 h-2 w-2 rounded-full bg-primary shrink-0" />
                  <span className="text-foreground/90">{i}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-foreground/90 leading-relaxed">{t.wellbeing.p3}</p>
            <p className="mt-4 text-foreground/90 leading-relaxed">{t.wellbeing.p4}</p>
          </div>
          <Img
            src="/wp-content/uploads/01.jpeg"
            alt={t.wellbeing.imgAlt}
            className="rounded-2xl w-full aspect-[4/3] object-cover"
            sizes={SIZES.split}
          />
        </div>
      </section>

      {/* Er du vores nye kollega */}
      <section className="bg-section-light text-section-light-foreground py-20">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.join.heading}</h2>
          <p className="mt-6 text-lg leading-relaxed">{t.join.p1}</p>
          <p className="mt-4 text-lg leading-relaxed">{t.join.p2}</p>
          <a
            href={EMPLY_JOBS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-10 btn-cta"
          >
            {t.join.cta} <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </SiteLayout>
  );
}
